import type { FunctionComponent } from 'react';
import { useIntl } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';
import { AddIcon } from '@trello/nachos/icons/add';
import { Popover, PopoverPlacement, usePopover } from '@trello/nachos/popover';
import { getTestId, type WorkspaceNavigationTestIds } from '@trello/test-ids';

import { LazyCreateWorkspaceViewPopoverScreenContent } from 'app/src/components/CreateWorkspaceViewPopoverScreen';

import styles from './CreateWorkspaceViewButton.less';

interface CreateWorkspaceViewButtonProps {
  workspaceId: string;
  enterpriseId?: string;
}

export const CreateWorkspaceViewButton: FunctionComponent<
  CreateWorkspaceViewButtonProps
> = ({ workspaceId, enterpriseId }) => {
  const intl = useIntl();

  const { triggerRef, toggle, popoverProps } = usePopover({
    placement: PopoverPlacement.RIGHT_START,
  });

  const onClick = () => {
    toggle();

    Analytics.sendClickedLinkEvent({
      linkName: 'createViewLink',
      source: 'boardSwitcherModal',
      containers: formatContainers({
        workspaceId,
        enterpriseId,
      }),
    });
  };

  return (
    <>
      <Button
        className={styles.button}
        ref={triggerRef}
        onClick={onClick}
        iconBefore={<AddIcon />}
        appearance="subtle"
        title={intl.formatMessage({
          id: 'templates.workspace_navigation.create-a-view',
          defaultMessage: 'Create a view',
          description: 'Title on create workspace view button',
        })}
        testId={getTestId<WorkspaceNavigationTestIds>(
          'create-workspace-view-button',
        )}
      />
      <Popover {...popoverProps}>
        <LazyCreateWorkspaceViewPopoverScreenContent
          idOrganization={workspaceId}
          idEnterprise={enterpriseId}
          location="BoardSwitcher"
        />
      </Popover>
    </>
  );
};
